import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchAdminLogs, fetchOverview } from './api';
import type { PlatformOverview } from './types';
import { fmtDate } from './utils';

interface AdminLogRow {
  id: number;
  type: string;
  action?: string;
  email?: string;
  endpoint: string;
  created_at: string;
}

const KPI_LINKS: Array<{
  key: keyof PlatformOverview['businesses'] | 'trials' | 'expiring' | 'paying' | 'billing_tickets' | 'urgent';
  label: string;
  warn?: boolean;
  to: string;
  value: (d: PlatformOverview) => number;
}> = [
  { key: 'total', label: 'Businesses', to: '/admin/businesses', value: (d) => d.businesses.total },
  { key: 'active', label: 'Active subs', to: '/admin/businesses?status=active', value: (d) => d.businesses.active },
  { key: 'payment_issues', label: 'Payment issues', warn: true, to: '/admin/businesses?filter=payment_issues', value: (d) => d.businesses.payment_issues },
  { key: 'trials', label: 'Active trials', to: '/admin/applications?status=trial', value: (d) => d.registrations.trial || 0 },
  { key: 'expiring', label: 'Trials expiring (3d)', warn: true, to: '/admin/businesses?filter=expiring_trial', value: (d) => d.trialsExpiringSoon },
  { key: 'paying', label: 'Paying apps', to: '/admin/applications?status=active', value: (d) => d.registrations.active || 0 },
  { key: 'billing_tickets', label: 'Billing tickets', warn: true, to: '/admin/billing', value: (d) => d.support.billing_open },
  { key: 'urgent', label: 'Urgent support', warn: true, to: '/admin/billing?urgent=1', value: (d) => d.support.urgent },
];

const OverviewPage: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [logs, setLogs] = useState<AdminLogRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [overview, recentLogs] = await Promise.all([
        fetchOverview(token),
        fetchAdminLogs(token, 8).catch(() => []),
      ]);
      setData(overview);
      setLogs(recentLogs);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  if (loading) return <div className="plat-empty">Loading platform overview…</div>;
  if (!data) return <div className="plat-empty">Could not load overview.</div>;

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>Platform overview</h2>
          <p>Click a metric to jump to the filtered list.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={load}>Refresh</button>
      </div>

      <div className="plat-kpi-row">
        {KPI_LINKS.map((kpi) => (
          <Link key={kpi.label} to={kpi.to} className={`plat-kpi plat-kpi-link${kpi.warn ? ' warn' : ''}`}>
            <div className="kl">{kpi.label}</div>
            <div className="kv">{kpi.value(data)}</div>
          </Link>
        ))}
      </div>

      <div className="plat-panel">
        <h3>Quick links</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/admin/businesses" className="plat-btn plat-btn-primary" style={{ textDecoration: 'none' }}>Manage businesses →</Link>
          <Link to="/admin/applications" className="plat-btn plat-btn-ghost" style={{ textDecoration: 'none' }}>Applications →</Link>
          <Link to="/admin/billing" className="plat-btn plat-btn-ghost" style={{ textDecoration: 'none' }}>Billing & support →</Link>
          <Link to="/admin/gdpr" className="plat-btn plat-btn-ghost" style={{ textDecoration: 'none' }}>GDPR tools →</Link>
          <Link to="/admin/system" className="plat-btn plat-btn-ghost" style={{ textDecoration: 'none' }}>System health →</Link>
        </div>
        <p className="plat-muted" style={{ marginTop: 14, marginBottom: 0 }}>
          Rate-limited API · All actions logged to Postgres <code style={{ color: '#94a3b8' }}>admin_logs</code> · Owner contact only by default
        </p>
      </div>

      <div className="plat-panel">
        <h3>Recent admin activity</h3>
        {logs.length === 0 ? (
          <p className="plat-muted" style={{ margin: 0 }}>No admin actions logged yet.</p>
        ) : (
          <div className="plat-table-wrap">
            <table className="plat-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Who</th>
                  <th>Endpoint</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="plat-muted">{fmtDate(log.created_at)}</td>
                    <td>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: log.type === 'unauthorized_access_attempt' ? '#f87171' : '#a5b4fc',
                      }}>
                        {log.type === 'unauthorized_access_attempt' ? 'Blocked' : `${log.action || 'GET'}`}
                      </span>
                    </td>
                    <td>{log.email || '—'}</td>
                    <td className="plat-muted" style={{ fontSize: 12 }}>{log.endpoint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default OverviewPage;
